import type { OrderDetails } from '../model'
import { withTutorialFallback } from '@/features/onboarding/lib'
import { apiFetch } from '@/shared/api'

/**
 * Fetches all orders for the current store/session.
 * If tutorial mode is active, returns mock data instead of making API call.
 */
export async function fetchOrders() {
  return withTutorialFallback<OrderDetails[]>(
    'orders',
    () => apiFetch<OrderDetails[]>('/orders'),
  )
}

/**
 * Fetches wait time information from the API using the application's apiFetch utility.
 * If tutorial mode is active, returns mock data instead of making API call.
 * @param timezoneOffset - The client's timezone offset in minutes.
 */
export async function getWaitTime(
  timezoneOffset?: number,
) {
  return await withTutorialFallback<{ waitTimeAverage: number, numberOfOrders: number }>(
    'waitTime',
    async () => {
      let endpoint = '/orders/waitTime'

      // Append timezoneOffset query parameter if it's a valid number
      if (typeof timezoneOffset === 'number' && !Number.isNaN(timezoneOffset)) {
        endpoint += `?timezoneOffset=${timezoneOffset}`
      }

      return apiFetch<{
        waitTimeAverage: number
        numberOfOrders: number
      }>(endpoint)
    },
  )
}

/**
 * Updates the status of an order
 *
 * @param params - The parameters for updating an order status
 * @param params.orderId - The ID of the order to update
 * @param params.newStatus - The new status to set
 * @param params.lastUpdateTime - Optional last update time for optimistic concurrency control
 * @param params.verificationData - Optional verification data for verified status changes
 * @param params.verificationData.verificationCode - Optional verification code for verified status changes
 * @param params.verificationData.verificationErrorCode - Optional error code when verification is not possible
 * @returns The updated order details
 */
export async function updateOrderStatus<T extends OrderDetails>({
  orderId,
  newStatus,
  lastUpdateTime,
  verificationData,
}: {
  orderId: string
  newStatus: string
  lastUpdateTime?: string
  verificationData?: {
    verificationCode?: string
    verificationErrorCode?: string
  }
}) {
  const url = `/orders/${orderId}`
  // Use JSON Patch format as per successful API request
  const patches = [
    {
      op: 'replace',
      path: '/status',
      value: newStatus,
    },
  ]

  // Add verification patch if provided
  if (verificationData) {
    if (verificationData.verificationCode) {
      patches.push({
        op: 'replace',
        path: '/verificationCode',
        value: verificationData.verificationCode,
      })
    }

    if (verificationData.verificationErrorCode) {
      patches.push({
        op: 'replace',
        path: '/verificationErrorCode',
        value: verificationData.verificationErrorCode,
      })
    }
  }

  return apiFetch<T>(url, {
    method: 'PATCH',
    body: patches,
    headers: lastUpdateTime ? {
      'x-bluedot-last-update-time': lastUpdateTime,
    } : undefined,
  })
}

/**
 * Acknowledges an order by setting isAcknowledged to true
 * @param params - The parameters for acknowledging an order
 * @param params.orderId - The ID of the order to acknowledge
 * @param params.lastUpdateTime - Optional last update time for optimistic concurrency control
 * @returns The updated order details
 */
export async function updateAcknowledgeOrder<T extends OrderDetails>({
  orderId,
  lastUpdateTime,
}: {
  orderId: string
  lastUpdateTime?: string
}): Promise<T> {
  const url = `/orders/${orderId}`
  const patches = [
    {
      op: 'replace',
      path: '/isAcknowledged',
      value: true,
    },
  ]

  return apiFetch<T>(url, {
    method: 'PATCH',
    body: patches,
    headers: lastUpdateTime ? {
      'x-bluedot-last-update-time': lastUpdateTime,
    } : undefined,
  })
}
